import { useEffect, useRef, useState } from "react";
import { Archive, ArchiveRestore, Download, Eye, Loader2, Pencil, Plus, RefreshCcw, Share2, Sparkles, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { envoyerParMorceaux, SEUIL_MORCEAUX } from "../../lib/uploadMorceaux";
import { FAMILY_NAMES } from "../../lib/localize";

// Carte « Liste de Prix coûtant par catalogue. » — demande du propriétaire
// (2026-10-20) : en haut de Système → Paramètres, ADMINISTRATEURS SEULEMENT
// (le backend l'impose avec require_admin et sert les fichiers par une route
// protégée, pas /api/files). Titre libre, consultation en ligne ou
// téléchargement, remplacement du fichier avec historique des versions.
// Règle du propriétaire : ARCHIVAGE SEULEMENT des listes — JAMAIS d'effacement.
// Partage : case « Voir les prix coûtants » PAR VENDEUR sur chaque liste —
// le vendeur choisi consulte alors cette liste précise dans son portail.

const TAILLE = (o) => (o >= 1048576 ? `${(o / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`);
const DATE = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" }) : "");

export default function PrixCoutantCard() {
  const [listes, setListes] = useState([]);
  const [vendeurs, setVendeurs] = useState([]);
  const [titre, setTitre] = useState("");
  const [fichier, setFichier] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState("");
  const [editTitre, setEditTitre] = useState("");
  const [editFichier, setEditFichier] = useState(null);
  const [progres, setProgres] = useState(null); // % d'envoi d'un gros fichier (par morceaux)
  // Pilule « Mettre à jour les coûtants du groupe » SUR CHAQUE liste (demande du
  // propriétaire, 2026-10-20 — version simplifiée) : la section de la boutique est
  // détectée toute seule par correspondance des codes ; aperçu des compteurs, puis
  // application avec rapport des éléments sans coûtant ou sans correspondance.
  const [majCible, setMajCible] = useState("");   // id de la liste en cours
  const [majData, setMajData] = useState(null);   // aperçu (compteurs) de la liste ciblée
  const [majRapport, setMajRapport] = useState(null); // rapport après application
  const [creerBusy, setCreerBusy] = useState("");     // création de fiches en cours (id liste)
  const [creerResult, setCreerResult] = useState(null); // {lid, nb_crees, existants, crees}
  const [majBusy, setMajBusy] = useState(false);
  // Choix MANUEL de la section (demande du propriétaire, 2026-10-21 : « ajouter
  // l'option de choisir dans quel section on veut mettre la liste de prix ») —
  // vide = détection automatique (défaut) ; la section choisie est imposée à
  // l'aperçu ET à l'application (les fiches créées y sont rangées).
  const [sections, setSections] = useState([]);
  const [majSection, setMajSection] = useState("");
  // Propositions de PRIX VENDANT par l'IA — À LA DEMANDE SEULEMENT (règle du
  // propriétaire, 2026-10-20) : la pilule calcule des propositions (coûtant ×
  // multiplicateur, arrondi au ,95 $, ajusté par Gemini) ; rien n'est écrit sans
  // le clic « Appliquer » — c'est le seul chemin IA qui touche un prix vendant.
  const [prixCible, setPrixCible] = useState("");
  const [prixJob, setPrixJob] = useState(null);
  const [prixProps, setPrixProps] = useState([]);
  const [prixBusy, setPrixBusy] = useState(false);
  // Case « facteur de mon choix » à côté de la pilule IA (demande du propriétaire,
  // 2026-10-20) : vide = le multiplicateur des Paramètres ; remplie = ce chiffre
  // (ex. : 2,5 ou 150 %) pour CE calcul seulement.
  const [facteurChoix, setFacteurChoix] = useState("");
  const fileRef = useRef(null);
  const editFileRef = useRef(null);

  const charger = async () => {
    try {
      const r = await api.get("/admin/prix-coutant");
      setListes(r.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const chargerVendeurs = async () => {
    try {
      const r = await api.get("/admin/vendeurs");
      setVendeurs(r.data.vendeurs || []);
    } catch { /* la liste des vendeurs reste vide — partage impossible sans elle */ }
  };
  const chargerSections = async () => {
    try {
      const r = await api.get("/admin/sections-boutique");
      setSections(r.data.sections || []);
    } catch { /* sans la liste, la liste déroulante reste sur « détection auto » */ }
  };
  useEffect(() => { charger(); chargerVendeurs(); chargerSections(); }, []);

  // Petit fichier → envoi direct ; gros (> 8 Mo) → par morceaux (taille illimitée)
  const joindreFichier = async (fd, f) => {
    if (f.size > SEUIL_MORCEAUX) {
      setProgres(0);
      const uid = await envoyerParMorceaux(f, setProgres);
      fd.append("upload_id", uid);
    } else {
      fd.append("file", f);
    }
  };

  const ajouter = async () => {
    if (!titre.trim()) { toast.error("Écrivez un titre pour la liste"); return; }
    if (!fichier) { toast.error("Choisissez le fichier de la liste de prix"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", titre.trim());
      await joindreFichier(fd, fichier);
      await api.post("/admin/prix-coutant", fd);
      toast.success(`Liste « ${titre.trim()} » ajoutée`);
      setTitre(""); setFichier(null);
      if (fileRef.current) fileRef.current.value = "";
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); setProgres(null); }
  };

  const sauverModif = async (id) => {
    if (!editTitre.trim() && !editFichier) { toast.error("Rien à modifier — titre ou fichier requis"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", editTitre.trim());
      if (editFichier) await joindreFichier(fd, editFichier);
      await api.put(`/admin/prix-coutant/${id}`, fd);
      toast.success("Liste modifiée");
      setEditId(""); setEditFichier(null);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); setProgres(null); }
  };

  const archiver = async (l) => {
    if (!window.confirm(`Archiver la liste « ${l.title} » ?\n\nElle n'est JAMAIS effacée : le fichier, l'historique des versions et le partage sont conservés. Vous pourrez la désarchiver en tout temps.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/prix-coutant/${l.id}/archiver`);
      toast.success(`Liste « ${l.title} » archivée`);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const desarchiver = async (l) => {
    setBusy(true);
    try {
      await api.post(`/admin/prix-coutant/${l.id}/desarchiver`);
      toast.success(`Liste « ${l.title} » remise dans la liste active`);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Case « Voir les prix coûtants » d'un vendeur sur CETTE liste précise
  const basculerPartage = async (l, email) => {
    const actuels = l.shared_with || [];
    const prochains = actuels.includes(email)
      ? actuels.filter((e) => e !== email)
      : [...actuels, email];
    // Mise à jour optimiste pour une case réactive
    setListes((ls) => ls.map((x) => (x.id === l.id ? { ...x, shared_with: prochains } : x)));
    try {
      await api.put(`/admin/prix-coutant/${l.id}/partage`, { emails: prochains });
      toast.success(prochains.includes(email)
        ? `${email} voit maintenant « ${l.title} » — avis envoyé par courriel`
        : `Accès de ${email} retiré pour « ${l.title} »`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      charger();
    }
  };

  const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none transition-colors";
  const urlVoir = (l) => `${API_BASE}/api/admin/prix-coutant/${l.id}/fichier`;

  // ---------- Pilule « Mettre à jour les coûtants du groupe » (par liste) ----------
  // section : "" = détection automatique (défaut) ; sinon section choisie dans
  // la liste déroulante de l'aperçu — imposée aussi à l'application
  const majPrevisualiser = async (l, section = "") => {
    setMajBusy(true); setMajCible(l.id); setMajData(null); setMajRapport(null);
    setMajSection(section);
    try {
      const r = await api.post(`/admin/prix-coutant/${l.id}/appliquer-au-groupe`, { apercu: true, section });
      setMajData(r.data);
    } catch (err) {
      setMajCible("");
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setMajBusy(false); }
  };

  // ---------- Créer les fiches BROUILLON des items sans fiche (demande du
  // propriétaire, 2026-10-20) : nom repris de la liste, coûtants remplis, prix
  // vendant à régler, photo envoyée plus tard ----------
  const creerFiches = async (lid) => {
    setCreerBusy(lid); setCreerResult(null);
    try {
      const r = await api.post(`/admin/prix-coutant/${lid}/creer-fiches`);
      setCreerResult({ lid, ...r.data });
      toast.success(r.data.nb_crees
        ? `${r.data.nb_crees} fiche(s) brouillon créée(s) — prix à régler, photo à venir`
        : "Aucune fiche à créer — tous les codes de la liste ont déjà leur fiche");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCreerBusy(""); }
  };

  // ---------- Pilule IA « Proposer des prix vendant » (par liste, à la demande) ----------
  // Plancher ×2,5 (règle du propriétaire, 2026-10-20) : un facteur plus bas est
  // refusé SAUF autorisation explicite d'un administrateur (confirm + forcer).
  const prixLancer = async (l, forcer = false) => {
    setPrixBusy(true); setPrixCible(l.id); setPrixProps([]); setPrixJob(null);
    try {
      await api.post(`/admin/prix-coutant/${l.id}/proposer-prix`, { facteur: facteurChoix.trim(), forcer });
      await prixRafraichir(l.id);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (!forcer && err.response?.status === 400 && detail.includes("×2,5")
          && window.confirm(`${detail}\n\nAutoriser ce facteur en tant qu'administrateur ?`)) {
        setPrixBusy(false);
        return prixLancer(l, true);
      }
      setPrixCible("");
      toast.error(formatApiError(detail));
    } finally { setPrixBusy(false); }
  };

  const prixRafraichir = async (lid) => {
    try {
      const r = await api.get(`/admin/prix-coutant/${lid}/propositions`);
      setPrixJob(r.data.job || {});
      setPrixProps(r.data.items || []);
    } catch { /* silencieux pendant le suivi */ }
  };

  // Suivi de la tâche de fond : relecture toutes les 3 s tant qu'elle calcule
  useEffect(() => {
    if (!prixCible || !(prixJob && prixJob.en_cours)) return undefined;
    const t = setInterval(() => prixRafraichir(prixCible), 3000);
    return () => clearInterval(t);
  }, [prixCible, prixJob?.en_cours]);

  const prixAppliquer = async (lid, ids = []) => {
    const n = ids.length || prixProps.length;
    if (!window.confirm(
      `Appliquer ${ids.length ? "cette proposition" : `les ${n} propositions`} ?\n\n` +
      "Le prix vendant des produits choisis sera REMPLACÉ par le prix proposé (et ses paliers vendant). " +
      "C'est votre demande directe — aucun autre prix ne bouge.")) return;
    setPrixBusy(true);
    try {
      const r = await api.post(`/admin/prix-coutant/${lid}/appliquer-propositions`, { ids });
      toast.success(`${r.data.appliques} prix vendant appliqué(s)`);
      await prixRafraichir(lid);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPrixBusy(false); }
  };

  const majAppliquer = async () => {
    if (!majData) return;
    if (!window.confirm(
      `Mettre à jour le groupe (section ${majData.section} — ${FAMILY_NAMES[majData.section] || "Boutique"}) depuis « ${majData.liste} » ?\n\n` +
      `• ${majData.mis_a_jour} coûtant(s) seront REMPLACÉS (prix à l'unité + paliers de quantité)\n` +
      ((majData.nb_maj_hors_section ?? 0) > 0
        ? `• ${majData.nb_maj_hors_section} coûtant(s) d'une autre section seront mis à jour (même code = même produit)\n` : "") +
      ((majData.nb_fiches_a_creer ?? 0) > 0
        ? `• ${majData.nb_fiches_a_creer} fiche(s) brouillon seront CRÉÉES (titre = description de la liste, code, coûtants — prix vendant plus tard)\n` : "") +
      "• Les codes absents de la liste gardent leur coûtant actuel\n" +
      "• Le rapport des éléments à vérifier s'affichera ensuite")) return;
    setMajBusy(true); setMajRapport(null);
    try {
      const r = await api.post(`/admin/prix-coutant/${majCible}/appliquer-au-groupe`, { apercu: false, section: majSection });
      if (r.data.en_arriere_plan || r.data.en_cours) {
        // Grosse liste → tâche de FOND (le délai Cloudflare serait dépassé en
        // synchrone — cas DTC, 2026-10-21) : on suit la progression par sondage
        setMajJob({ en_cours: true, phase: "lecture" });
        await majJobRafraichir(majCible);
      } else {
        setMajRapport(r.data); setMajData(null);
        toast.success(`${r.data.mis_a_jour} coûtant(s) mis à jour dans la section ${r.data.section}`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setMajBusy(false); }
  };

  // Suivi de la tâche de fond « Mettre à jour les coûtants » (grosses listes)
  const [majJob, setMajJob] = useState(null);
  const majJobRafraichir = async (lid) => {
    try {
      const r = await api.get(`/admin/prix-coutant/${lid}/maj-job`);
      const j = r.data.job || {};
      setMajJob(j);
      if (!j.en_cours) {
        if (j.rapport) {
          setMajRapport(j.rapport); setMajData(null);
          toast.success(`${j.rapport.mis_a_jour} coûtant(s) mis à jour dans la section ${j.rapport.section}`
            + (j.rapport.nb_fiches_creees ? ` — ${j.rapport.nb_fiches_creees} fiche(s) brouillon créée(s)` : ""));
        } else if (j.erreur) {
          toast.error(`La mise à jour a échoué : ${j.erreur}`);
        }
      }
    } catch { /* silencieux pendant le suivi */ }
  };
  // Sondage toutes les 3 s tant que la mise à jour tourne en arrière-plan
  useEffect(() => {
    if (!majCible || !(majJob && majJob.en_cours)) return undefined;
    const t = setInterval(() => majJobRafraichir(majCible), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [majCible, majJob?.en_cours]);
  const btnCls = "inline-flex items-center whitespace-nowrap gap-1.5 border border-ivory/25 text-ivory/70 text-xs px-3.5 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors";
  const actives = listes.filter((l) => !l.archived);
  const archivees = listes.filter((l) => l.archived);

  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-8 space-y-4" data-testid="prix-coutant-card">
      <div>
        <p className="font-semibold text-gold">Liste de Prix coûtant par catalogue.</p>
        <p className="text-xs text-ivory/70 mt-1">
          Géré par les administrateurs — jamais affiché aux clients. Téléversez une liste
          (PDF, image, Excel ou CSV — <strong>aucune limite de taille</strong>, les gros
          fichiers partent automatiquement par morceaux avec une barre de progression),
          consultez-la en ligne ou téléchargez-la, remplacez-la
          (l'ancienne version est conservée). Les listes sont <strong>archivées, jamais
          effacées</strong>. La case « Voir les prix coûtants » donne à un vendeur choisi
          l'accès à cette liste précise, en lecture seule.
        </p>
      </div>

      {/* Ajout d'une liste */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={200}
          placeholder="Titre de la liste (ex. : Prix coûtant Caldwell 2026)"
          data-testid="prix-coutant-titre" className={`${inputCls} sm:flex-1`} />
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv"
          onChange={(e) => setFichier(e.target.files?.[0] || null)}
          data-testid="prix-coutant-fichier"
          className="text-xs text-ivory/70 file:mr-3 file:rounded-full file:border-0 file:bg-ivory/10 file:px-3 file:py-1.5 file:text-xs file:text-ivory hover:file:bg-gold hover:file:text-ink file:transition-colors sm:max-w-[240px]" />
        <button type="button" onClick={ajouter} disabled={busy} data-testid="prix-coutant-ajouter"
          className="inline-flex items-center justify-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors disabled:opacity-60 shrink-0">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {progres !== null ? `Envoi… ${progres} %` : "Ajouter la liste"}
        </button>
      </div>
      {progres !== null && (
        <div className="h-1.5 bg-ivory/10 rounded-full overflow-hidden" data-testid="prix-coutant-progression">
          <div className="h-full bg-gold transition-all duration-300" style={{ width: `${progres}%` }} />
        </div>
      )}

      {/* Listes actives */}
      {actives.length === 0 ? (
        <p className="text-xs text-ivory/45" data-testid="prix-coutant-vide">Aucune liste active pour l'instant — ajoutez la première ci-dessus.</p>
      ) : (
        <div className="space-y-2">
          {actives.map((l) => (
            <div key={l.id} className="border border-ivory/10 rounded-lg px-4 py-3" data-testid={`prix-coutant-item-${l.id}`}>
              {editId === l.id ? (
                <div className="space-y-2">
                  <input value={editTitre} onChange={(e) => setEditTitre(e.target.value)} maxLength={200}
                    placeholder="Nouveau titre" data-testid={`prix-coutant-edit-titre-${l.id}`} className={inputCls} />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input ref={editFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv"
                      onChange={(e) => setEditFichier(e.target.files?.[0] || null)}
                      data-testid={`prix-coutant-edit-fichier-${l.id}`}
                      className="text-xs text-ivory/70 file:mr-3 file:rounded-full file:border-0 file:bg-ivory/10 file:px-3 file:py-1.5 file:text-xs file:text-ivory hover:file:bg-gold hover:file:text-ink file:transition-colors sm:max-w-[260px]" />
                    <div className="flex gap-2 shrink-0">
                      <button type="button" onClick={() => sauverModif(l.id)} disabled={busy} data-testid={`prix-coutant-save-${l.id}`}
                        className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors disabled:opacity-60">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {progres !== null ? `Envoi… ${progres} %` : "Enregistrer"}
                      </button>
                      <button type="button" onClick={() => { setEditId(""); setEditFichier(null); }} data-testid={`prix-coutant-annuler-${l.id}`}
                        className={btnCls}>
                        <X className="w-3 h-3" /> Annuler
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-ivory/45">Choisir un fichier remplace l'ancien (conservé dans les versions) ; laisser vide garde le fichier actuel.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" data-testid={`prix-coutant-item-titre-${l.id}`}>{l.title}</p>
                    <p className="text-xs text-ivory/55 truncate">{l.filename} · {TAILLE(l.size)} · {DATE(l.updated_at || l.created_at)}</p>
                  </div>
                  {/* Pilules ENCADRÉES (demande du propriétaire, 2026-10-20) :
                      1re ligne = Télécharger / Modifier / Voir en ligne / Archiver ;
                      2e ligne = mise à jour du groupe + facteur + proposition IA */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                    <a href={`${urlVoir(l)}?dl=1`} data-testid={`prix-coutant-telecharger-${l.id}`}
                      className={`${btnCls} justify-center`}>
                      <Download className="w-3.5 h-3.5" /> Télécharger
                    </a>
                    <button type="button" onClick={() => { setEditId(l.id); setEditTitre(l.title); setEditFichier(null); }}
                      data-testid={`prix-coutant-modifier-${l.id}`} className={`${btnCls} justify-center`}>
                      <Pencil className="w-3.5 h-3.5" /> Modifier
                    </button>
                    <a href={urlVoir(l)} target="_blank" rel="noopener noreferrer" data-testid={`prix-coutant-voir-${l.id}`}
                      className="inline-flex items-center justify-center whitespace-nowrap gap-1.5 border border-emerald-500/50 text-emerald-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-emerald-500 hover:text-ink transition-colors">
                      <Eye className="w-3.5 h-3.5" /> Voir en ligne
                    </a>
                    {/* ARCHIVAGE SEULEMENT — jamais d'effacement (règle du propriétaire) */}
                    <button type="button" onClick={() => archiver(l)} disabled={busy} data-testid={`prix-coutant-archiver-${l.id}`}
                      className="inline-flex items-center justify-center whitespace-nowrap gap-1.5 border border-amber-500/40 text-amber-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-amber-500 hover:text-ink transition-colors disabled:opacity-60">
                      <Archive className="w-3.5 h-3.5" /> Archiver
                    </button>
                  </div>
                  {/* 2e ligne : mise à jour du groupe + facteur de votre choix + IA */}
                  <div className="flex flex-wrap gap-2 w-full">
                    {/* Pilule « Mettre à jour les coûtants du groupe » (demande du
                        propriétaire, 2026-10-20) : applique CETTE liste à la section
                        de la boutique détectée automatiquement (correspondance des codes) */}
                    <button type="button" onClick={() => majPrevisualiser(l)} disabled={majBusy}
                      data-testid={`prix-coutant-maj-${l.id}`}
                      className="inline-flex items-center justify-center gap-1.5 border border-sky-400/50 text-sky-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-sky-400 hover:text-ink transition-colors disabled:opacity-60">
                      {majBusy && majCible === l.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCcw className="w-3.5 h-3.5" />}
                      Mettre à jour les coûtants du groupe
                    </button>
                    {/* Pilule IA « Proposer des prix vendant » — À LA DEMANDE SEULEMENT
                        (règle du propriétaire, 2026-10-20) : propose depuis les paliers de
                        coûtant (× multiplicateur, arrondi ,95 $) ; rien n'est appliqué
                        sans le clic « Appliquer » */}
                    {/* Case « facteur de mon choix » (demande du propriétaire,
                        2026-10-20) : vide = multiplicateur des Paramètres ; remplie =
                        ce chiffre pour CE calcul (ex. : 2,5 ou 150 %) */}
                    <input value={facteurChoix} onChange={(e) => setFacteurChoix(e.target.value)}
                      placeholder="facteur (min. ×2,5)" maxLength={10}
                      title="Facteur de votre choix (ex. : 3, 2,5 ou 150 %) — minimum ×2,5, jamais plus bas (sauf autorisation admin) ; vide = le multiplicateur réglé dans Paramètres"
                      data-testid={`prix-coutant-ia-facteur-${l.id}`}
                      className="w-full sm:w-28 bg-ink border border-violet-400/40 rounded-full px-3 py-1.5 text-xs text-center text-violet-200 placeholder:text-violet-200/35 focus:border-violet-300 focus:outline-none transition-colors" />
                    <button type="button" onClick={() => prixLancer(l)} disabled={prixBusy}
                      data-testid={`prix-coutant-ia-${l.id}`}
                      className="inline-flex items-center justify-center gap-1.5 border border-violet-400/50 text-violet-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-violet-400 hover:text-ink transition-colors disabled:opacity-60">
                      {prixBusy && prixCible === l.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                      Proposer des prix vendant (IA)
                    </button>
                  </div>
                </div>
              )}

              {/* Aperçu puis rapport de la mise à jour des coûtants du groupe */}
              {majCible === l.id && majBusy && !majData && !majRapport && (
                <p className="text-[11px] text-ivory/60 mt-2 flex items-center gap-2" data-testid={`prix-coutant-maj-lecture-${l.id}`}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Lecture de la liste — la première
                  lecture d'une grosse liste peut prendre une minute (ensuite c'est instantané)…
                </p>
              )}
              {majCible === l.id && majData && (
                <div className="mt-2 text-xs bg-ink/60 border border-sky-400/30 rounded-lg p-3 space-y-2" data-testid={`prix-coutant-maj-apercu-${l.id}`}>
                  {majData.besoin_choix_section ? (
                    <p className="text-amber-300/90" data-testid={`prix-coutant-besoin-section-${l.id}`}>
                      Aucune section ne correspond automatiquement aux {majData.codes_liste} codes de
                      « {majData.liste} » — <strong>choisissez la section ci-dessous</strong> pour y
                      appliquer cette liste :
                    </p>
                  ) : (
                    <p className="text-ivory/80">
                      {majData.section_forcee ? "Section choisie" : "Groupe détecté"} : <strong className="text-gold">section {majData.section} — {FAMILY_NAMES[majData.section] || "Boutique"}</strong> ({majData.produits_section} produits)
                    </p>
                  )}
                  {/* Choix MANUEL de la section (demande du propriétaire,
                      2026-10-21) : changer la section relance l'aperçu pour ce
                      groupe ; la même section sera utilisée à l'application */}
                  <label className="flex items-center gap-2 text-[11px] text-ivory/70 flex-wrap">
                    Mettre la liste dans la section :
                    <select value={majSection}
                      onChange={(e) => majPrevisualiser(l, e.target.value)}
                      disabled={majBusy}
                      data-testid={`prix-coutant-maj-section-${l.id}`}
                      className="bg-ink border border-ivory/20 rounded-lg px-2 py-1 text-xs text-ivory focus:border-gold focus:outline-none">
                      <option value="">{majData.besoin_choix_section ? "— Choisir une section —" : `Détection automatique (section ${majData.section})`}</option>
                      {sections.map((s) => (
                        <option key={s.section} value={s.section}>
                          Section {s.section} — {FAMILY_NAMES[s.section] || "Boutique"} ({s.produits > 0 ? `${s.produits} produits` : "vide — les fiches y seront créées"})
                        </option>
                      ))}
                    </select>
                  </label>
                  {!majData.besoin_choix_section && (
                    <p className="text-ivory/75">
                      <strong className="text-gold">{majData.mis_a_jour}</strong> coûtant(s) seront REMPLACÉS
                      (prix à l'unité + paliers de quantité, quantité de caisse indiquée) ·{" "}
                      <strong className="text-amber-300">{majData.nb_a_verifier}</strong> produit(s) sans
                      correspondance (coûtant conservé) · <strong>{majData.nb_codes_sans_produit}</strong> code(s)
                      de la liste sans produit nulle part dans la boutique.
                    </p>
                  )}
                  {(majData.nb_maj_hors_section ?? 0) > 0 && (
                    <p className="text-[11px] text-sky-300/90" data-testid={`prix-coutant-apercu-hors-section-${l.id}`}>
                      <strong>{majData.nb_maj_hors_section}</strong> code(s) correspondent à un produit d'une
                      AUTRE section — même code = même produit : leur coûtant sera mis à jour
                      (aucune fiche dupliquée).
                    </p>
                  )}
                  {(majData.nb_couverts_famille ?? 0) > 0 && (
                    <p className="text-[11px] text-ivory/55">
                      <strong>{majData.nb_couverts_famille}</strong> code(s) de famille (joker « xx ») ou variantes
                      déjà couverts par des produits existants — rien à créer ni à changer pour eux.
                    </p>
                  )}
                  {/* Demande du propriétaire (2026-10-20/21) : l'application CRÉE les
                      fiches manquantes — titre = description de la liste, code,
                      coûtant à l'unité + prix dégressifs + qté de caisse ;
                      brouillon sans prix vendant (réglé plus tard, sur demande) */}
                  {(majData.nb_fiches_a_creer ?? 0) > 0 && (
                    <p className="text-[11px] text-emerald-300/90">
                      À l'application : <strong>{majData.nb_fiches_a_creer}</strong> fiche(s)
                      brouillon seront CRÉÉES (titre = description de la liste, code, coûtant
                      à l'unité + prix dégressifs + qté de caisse ; prix vendant à 0 $ à régler,
                      photo plus tard).
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {!majData.besoin_choix_section && (
                      <button type="button" onClick={majAppliquer} disabled={majBusy} data-testid={`prix-coutant-maj-confirmer-${l.id}`}
                        className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors disabled:opacity-60">
                        {majBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                        Appliquer la mise à jour{(majData.nb_fiches_a_creer ?? 0) > 0 ? ` + créer les ${majData.nb_fiches_a_creer} fiches` : ""}
                      </button>
                    )}
                    <button type="button" onClick={() => { setMajCible(""); setMajData(null); setMajSection(""); }} data-testid={`prix-coutant-maj-annuler-${l.id}`} className={btnCls}>
                      <X className="w-3 h-3" /> Annuler
                    </button>
                  </div>
                </div>
              )}
              {majCible === l.id && majJob?.en_cours && (
                <p className="mt-2 text-[11px] text-ivory/60 flex items-center gap-2" data-testid={`prix-coutant-maj-job-${l.id}`}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Mise à jour en cours en arrière-plan
                  {majJob.phase === "lecture" && " — lecture de la liste…"}
                  {majJob.phase === "mise_a_jour" && " — mise à jour des coûtants…"}
                  {majJob.phase === "creation_fiches" && ` — création des fiches (${majJob.a_creer ?? "…"} à créer)…`}
                  {" "}Le rapport s'affichera ici automatiquement.
                </p>
              )}
              {majCible === l.id && majRapport && (
                <div className="mt-2 bg-ink/60 border border-gold/25 rounded-lg p-4 space-y-2" data-testid={`prix-coutant-maj-rapport-${l.id}`}>
                  <p className="text-xs text-ivory/80">
                    <strong className="text-gold">{majRapport.mis_a_jour}</strong> coûtant(s) mis à jour dans la
                    section {majRapport.section} depuis « {majRapport.liste} ».
                  </p>
                  {/* Codes de la liste dont le produit existe dans une AUTRE
                      section (règle 2026-10-21) : coûtant mis à jour, JAMAIS de
                      fiche dupliquée */}
                  {(majRapport.nb_maj_hors_section ?? 0) > 0 && (
                    <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3" data-testid={`prix-coutant-hors-section-${l.id}`}>
                      <p className="text-xs text-sky-300">
                        <strong>{majRapport.nb_maj_hors_section}</strong> coûtant(s) mis à jour sur des produits
                        d'une AUTRE section (même code = même produit — aucune fiche dupliquée) :
                      </p>
                      <ul className="mt-1.5 max-h-32 overflow-y-auto space-y-0.5 pr-1">
                        {majRapport.maj_hors_section.map((c) => (
                          <li key={c.id} className="text-[11px] text-ivory/70" data-testid={`prix-coutant-hors-section-item-${c.id}`}>
                            <span className="text-gold font-mono">{c.sku}</span> — {c.name}
                            <span className="text-ivory/45"> (section {String(c.catalogue_no || "").split(".")[0]})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-amber-300/90 flex items-center gap-1.5">
                    <TriangleAlert className="w-3.5 h-3.5 shrink-0" />
                    {majRapport.nb_a_verifier} élément(s) du groupe sans coûtant ou dont le code ne correspond pas :
                  </p>
                  {majRapport.elements_a_verifier.length > 0 ? (
                    <ul className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid={`prix-coutant-maj-liste-${l.id}`}>
                      {majRapport.elements_a_verifier.map((e) => (
                        <li key={e.id} className="text-[11px] text-ivory/70 border-b border-ivory/5 pb-1" data-testid={`prix-coutant-maj-item-${e.id}`}>
                          <span className="text-ivory/90">{e.name}</span>
                          {e.sku ? <span className="text-ivory/45"> — {e.sku}</span> : null}
                          <span className="block text-[10px] text-amber-300/70">{e.raison}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-emerald-300/80">Aucun — tous les produits du groupe correspondent à la liste.</p>
                  )}
                  {/* Codes de FAMILLE (joker xx) / variantes déjà couverts par
                      des produits existants — rapportés à part (2026-10-21) */}
                  {(majRapport.nb_couverts_famille ?? 0) > 0 && (
                    <p className="text-[11px] text-ivory/55" data-testid={`prix-coutant-famille-${l.id}`}>
                      {majRapport.nb_couverts_famille} code(s) de famille (joker « xx ») ou variantes déjà
                      couverts par des produits existants — aucune fiche à créer pour eux.
                    </p>
                  )}
                  {/* Fiches BROUILLON créées automatiquement par la mise à jour
                      (demande du propriétaire, 2026-10-20) : nom + SKU + coûtant,
                      prix vendant à régler sur une autre demande, photo à venir */}
                  {majRapport.nb_fiches_creees > 0 && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid={`prix-coutant-fiches-creees-${l.id}`}>
                      <p className="text-xs text-emerald-300">
                        <strong>{majRapport.nb_fiches_creees}</strong> fiche(s) brouillon créée(s) automatiquement
                        (invisibles en boutique — nom de la liste, coûtant rempli, prix vendant à 0 $ à régler, photo à venir) :
                      </p>
                      <ul className="mt-1.5 max-h-40 overflow-y-auto space-y-0.5 pr-1">
                        {majRapport.fiches_creees.map((c) => (
                          <li key={c.id} className="text-[11px] text-ivory/70">
                            <span className="text-gold font-mono">{c.sku}</span> — {c.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {creerResult?.lid === l.id && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid={`prix-coutant-creer-resultat-${l.id}`}>
                      <p className="text-xs text-emerald-300">
                        <strong>{creerResult.nb_crees}</strong> fiche(s) brouillon créée(s)
                        {creerResult.existants > 0 && ` — ${creerResult.existants} code(s) avaient déjà une fiche`}.
                      </p>
                      {creerResult.crees.length > 0 && (
                        <ul className="mt-1.5 max-h-40 overflow-y-auto space-y-0.5 pr-1">
                          {creerResult.crees.map((c) => (
                            <li key={c.id} className="text-[11px] text-ivory/70">
                              <span className="text-gold font-mono">{c.sku}</span> — {c.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <button type="button" onClick={() => { setMajCible(""); setMajRapport(null); }}
                    data-testid={`prix-coutant-maj-fermer-${l.id}`} className={btnCls}>
                    <X className="w-3 h-3" /> Fermer le rapport
                  </button>
                </div>
              )}

              {/* Propositions de prix vendant IA — À LA DEMANDE SEULEMENT :
                  progression du calcul, puis tableau des propositions ; rien
                  n'est écrit sans le clic « Appliquer » (règle du propriétaire) */}
              {prixCible === l.id && prixJob?.en_cours && (
                <p className="text-[11px] text-violet-300/90 mt-2 flex items-center gap-2" data-testid={`prix-coutant-ia-progression-${l.id}`}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  L'IA calcule les propositions… {prixJob.traites || 0}/{prixJob.total || "?"} produits
                </p>
              )}
              {prixCible === l.id && prixJob && !prixJob.en_cours && (
                <div className="mt-2 bg-ink/60 border border-violet-400/30 rounded-lg p-4 space-y-2" data-testid={`prix-coutant-ia-table-${l.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-ivory/80">
                      <strong className="text-violet-300">{prixProps.length}</strong> proposition(s) de prix vendant
                      (×{prixProps[0]?.multiplicateur ?? 3}, arrondi au ,95 $) — <strong>rien n'est appliqué</strong> sans votre clic.
                    </p>
                    {prixProps.length > 0 && (
                      <button type="button" onClick={() => prixAppliquer(l.id)} disabled={prixBusy}
                        data-testid={`prix-coutant-ia-tout-appliquer-${l.id}`}
                        className="inline-flex items-center gap-1.5 bg-violet-400 text-ink text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-violet-300 transition-colors disabled:opacity-60">
                        {prixBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        Tout appliquer ({prixProps.length})
                      </button>
                    )}
                  </div>
                  {prixProps.length === 0 ? (
                    <p className="text-[11px] text-ivory/50">Aucune proposition en attente — cliquez la pilule IA pour en calculer.</p>
                  ) : (
                    <ul className="max-h-64 overflow-y-auto divide-y divide-ivory/5 pr-1" data-testid={`prix-coutant-ia-liste-${l.id}`}>
                      {prixProps.map((pr) => (
                        <li key={pr.id} className="py-1.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`prix-coutant-ia-prop-${pr.id}`}>
                          <span className="text-[11px] text-ivory/80 min-w-0">
                            <span className="block truncate">{pr.name}{pr.sku ? <span className="text-ivory/40"> — {pr.sku}</span> : null}</span>
                            <span className="text-ivory/45">
                              {pr.ancien != null ? `${Number(pr.ancien).toFixed(2)} $` : "—"} →{" "}
                              <strong className="text-violet-300">{Number(pr.propose).toFixed(2)} $</strong>
                              {pr.tiers?.length > 0 && (
                                <span> · paliers : {pr.tiers.map((t) => `${t.qty}+ ${Number(t.propose).toFixed(2)} $`).join(", ")}</span>
                              )}
                            </span>
                          </span>
                          <button type="button" onClick={() => prixAppliquer(l.id, [pr.id])} disabled={prixBusy}
                            data-testid={`prix-coutant-ia-appliquer-${pr.id}`}
                            className="text-[10px] border border-violet-400/50 text-violet-300 px-3 py-1 rounded-full hover:bg-violet-400 hover:text-ink transition-colors disabled:opacity-60 shrink-0">
                            Appliquer
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button type="button" onClick={() => { setPrixCible(""); setPrixJob(null); setPrixProps([]); }}
                    data-testid={`prix-coutant-ia-fermer-${l.id}`} className={btnCls}>
                    <X className="w-3 h-3" /> Fermer
                  </button>
                </div>
              )}

              {/* Partage par vendeur : case « Voir les prix coûtants » par vendeur
                  pour CETTE liste précise (demande du propriétaire, 2026-10-20) */}
              {vendeurs.length > 0 && (
                <details className="mt-2" data-testid={`prix-coutant-partage-${l.id}`}>
                  <summary className="text-[11px] text-ivory/55 cursor-pointer hover:text-gold transition-colors select-none w-fit inline-flex items-center gap-1.5">
                    <Share2 className="w-3 h-3" /> Partagé avec les vendeurs ({(l.shared_with || []).length})
                  </summary>
                  <div className="mt-1.5 space-y-1 pl-3 border-l border-ivory/10">
                    {vendeurs.map((v) => (
                      <label key={v.id} className="flex items-center gap-2 text-[11px] text-ivory/70 cursor-pointer hover:text-gold transition-colors w-fit"
                        data-testid={`prix-coutant-partage-${l.id}-${v.id}`}>
                        <input type="checkbox" checked={(l.shared_with || []).includes(v.email)}
                          onChange={() => basculerPartage(l, v.email)}
                          data-testid={`prix-coutant-partage-case-${l.id}-${v.id}`}
                          className="accent-[#C9A84C] w-3.5 h-3.5 cursor-pointer" />
                        <span>{v.name || v.email} — Voir les prix coûtants</span>
                      </label>
                    ))}
                    <p className="text-[10px] text-ivory/40">Coché = ce vendeur consulte cette liste (lecture seule) dans son portail, onglet Paramètres. Un courriel l'avise automatiquement quand l'accès est activé.</p>
                  </div>
                </details>
              )}

              {/* Historique des versions remplacées : l'ancienne liste reste consultable */}
              {l.versions?.length > 0 && (
                <details className="mt-2" data-testid={`prix-coutant-versions-${l.id}`}>
                  <summary className="text-[11px] text-ivory/55 cursor-pointer hover:text-gold transition-colors select-none w-fit">
                    Versions précédentes ({l.versions.length})
                  </summary>
                  <div className="mt-1.5 space-y-1 pl-3 border-l border-ivory/10">
                    {l.versions.map((v, i) => (
                      <p key={i} className="text-[11px] text-ivory/60 flex items-center gap-2 flex-wrap">
                        <span className="truncate max-w-[200px]">{v.filename}</span>
                        <span className="text-ivory/40">remplacée le {DATE(v.replaced_at)}{v.replaced_by ? ` par ${v.replaced_by}` : ""}</span>
                        <a href={`${urlVoir(l)}?version=${i}`} target="_blank" rel="noopener noreferrer"
                          data-testid={`prix-coutant-version-${l.id}-${i}`}
                          className="text-gold underline underline-offset-2 hover:text-champagne">Voir</a>
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Listes archivées — conservées à vie (fichier, versions, partage),
          désarchivables en un clic */}
      {archivees.length > 0 && (
        <details className="pt-2 border-t border-ivory/10" data-testid="prix-coutant-archives">
          <summary className="text-xs text-ivory/55 cursor-pointer hover:text-gold transition-colors select-none w-fit inline-flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" /> Listes archivées ({archivees.length}) — conservées, jamais effacées
          </summary>
          <div className="mt-2 space-y-2">
            {archivees.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 flex-wrap border border-ivory/10 rounded-lg px-4 py-3 opacity-75"
                data-testid={`prix-coutant-archive-${l.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-ivory/70">{l.title}</p>
                  <p className="text-xs text-ivory/50 truncate">
                    {l.filename} · {TAILLE(l.size)} · archivée le {DATE(l.archived_at)}{l.archived_by ? ` par ${l.archived_by}` : ""}
                    {l.versions?.length ? ` · ${l.versions.length} version(s) conservée(s)` : ""}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:justify-end shrink-0">
                  <a href={urlVoir(l)} target="_blank" rel="noopener noreferrer" data-testid={`prix-coutant-archive-voir-${l.id}`}
                    className="inline-flex items-center justify-center whitespace-nowrap gap-1.5 border border-emerald-500/50 text-emerald-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-emerald-500 hover:text-ink transition-colors">
                    <Eye className="w-3.5 h-3.5" /> Voir
                  </a>
                  <button type="button" onClick={() => desarchiver(l)} disabled={busy} data-testid={`prix-coutant-desarchiver-${l.id}`} className={`${btnCls} justify-center`}>
                    <ArchiveRestore className="w-3.5 h-3.5" /> Désarchiver
                  </button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
